import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Hero } from "@/components/Hero";
import { OperationalCenter } from "@/components/OperationalCenter";
import { InterventoRecap } from "@/components/InterventoRecap";
import { ProgettiInEvidenza } from "@/components/ProgettiInEvidenza";
import { BannerVolontari } from "@/components/BannerVolontari";
import { OlioSection } from "@/components/OlioSection";
import { NewsCard } from "@/components/NewsCard";
import { ProjectCard } from "@/components/ProjectCard";
import { ArrowRight, CalendarDays, Newspaper, Mountain, Heart, ChevronDown, Users, Sprout, Flame } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const VALUES = [
  { word: "tutela", desc: "del territorio" },
  { word: "partecipazione", desc: "civica attiva" },
  { word: "trasparenza", desc: "verso i cittadini" },
  { word: "collaborazione", desc: "con istituzioni" },
];

export default function Home() {
  const [news, setNews] = useState([]);
  const [events, setEvents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [stats, setStats] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${API}/api/news`).then((r) => r.json()).catch(() => []),
      fetch(`${API}/api/events`).then((r) => r.json()).catch(() => []),
      fetch(`${API}/api/projects`).then((r) => r.json()).catch(() => []),
      fetch(`${API}/api/faq`).then((r) => r.json()).catch(() => []),
      fetch(`${API}/api/public-stats`).then((r) => r.json()).catch(() => null),
    ]).then(([n, e, p, f, s]) => {
      setNews(Array.isArray(n) ? n.slice(0, 6) : []);
      setEvents(Array.isArray(e) ? e.slice(0, 6) : []);
      setProjects(Array.isArray(p) ? p.slice(0, 4) : []);
      setFaqs(Array.isArray(f) ? f.slice(0, 6) : []);
      setStats(s);
    });
  }, []);

  return (
    <Layout transparentHeader>
      <Hero />

      {/* Intervento 11 Luglio — subito dopo la hero */}
      <InterventoRecap />

      {/* Banner volontari */}
      <BannerVolontari />

      {/* Raccolta olive / olio dell'associazione */}
      <OlioSection />

      {/* Centro Operativo del Territorio */}
      <OperationalCenter />

      {/* I progetti che stiamo costruendo */}
      <ProgettiInEvidenza />

      {/* SECTION 1 — News */}
      <section data-testid="home-news" className="relative py-20 sm:py-28 bg-[#FBF9F6] border-t border-[#E5E1D8]">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between gap-6 mb-10 sm:mb-14">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-[#C15C3D]" />
                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#C15C3D]">
                  Le ultime novità
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#1A1A1A] leading-[1.05] tracking-tight">
                Dal cuore <span className="italic font-light text-[#2E4F3B]">di Asciano.</span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
                Notizie, aggiornamenti e iniziative dell'associazione.
              </p>
            </div>
            <Link
              to="/news"
              data-testid="home-news-all"
              className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-[#1A1A1A] hover:text-[#2E4F3B] underline underline-offset-4"
            >
              Tutte le novità
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {news.length === 0 ? (
            <div className="text-center py-12 text-[#5C5C5C] italic">Nessuna notizia disponibile al momento.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
              {news.map((n) => (
                <NewsCard key={n.id} news={n} showEyebrow testidPrefix="home-news-card" />
              ))}
            </div>
          )}

          <div className="mt-10 sm:hidden text-center">
            <Link to="/news" className="inline-flex items-center gap-2 text-sm font-bold text-[#1A1A1A] underline">
              Tutte le novità
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 2 — Values strip */}
      <section data-testid="home-values" className="relative py-16 sm:py-20 bg-[#1f2d22] text-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0 opacity-[0.04]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "30px 30px" }} />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <p className="font-display text-2xl sm:text-3xl lg:text-4xl font-light leading-tight tracking-tight">
            Crediamo in una <span className="italic text-[#E8C9A0]">gestione responsabile</span> e{" "}
            <span className="italic text-[#E8C9A0]">partecipativa</span> del territorio.
            <br />
            Lavoriamo per un equilibrio tra{" "}
            <span className="italic text-[#E8C9A0]">tutela ambientale</span>, comunità e futuro.
          </p>
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-10">
            {VALUES.map((v, i) => (
              <div key={v.word} className="text-left sm:text-center">
                <div className="font-display text-3xl sm:text-4xl font-black text-[#E8C9A0] leading-none tabular-nums">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-3 font-display text-base sm:text-lg font-bold uppercase tracking-wider">{v.word}</div>
                <div className="text-xs sm:text-sm text-white/65 mt-1">{v.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 3 — Eventi */}
      <section data-testid="home-events" className="relative py-20 sm:py-28 bg-white overflow-hidden">
        <div className="pointer-events-none absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-[#2E4F3B]/[0.05] blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between gap-6 mb-10 sm:mb-14">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-[#C15C3D]" />
                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#C15C3D]">
                  Prossimi eventi
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#1A1A1A] leading-[1.05] tracking-tight">
                Calendario <span className="italic font-light text-[#2E4F3B]">comunitario.</span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
                Gli appuntamenti, gli incontri e gli eventi dell'associazione.
              </p>
            </div>
            <Link to="/eventi" data-testid="home-events-all" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-[#1A1A1A] hover:text-[#2E4F3B] underline underline-offset-4">
              Tutti gli eventi
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {events.length === 0 ? (
            <div className="text-center py-12 text-[#5C5C5C] italic">Nessun evento in programma.</div>
          ) : (
            <div className="space-y-4">
              {events.map((e) => (
                <article
                  key={e.id}
                  data-testid={`home-event-${e.slug}`}
                  className="group grid sm:grid-cols-12 gap-5 sm:gap-8 items-center bg-[#FBF9F6] border-2 border-[#E5E1D8] rounded-2xl p-5 sm:p-7 hover:border-[#2E4F3B] transition-all"
                >
                  <div className="sm:col-span-3 flex sm:flex-col items-center sm:items-start gap-4 sm:gap-1 sm:border-r sm:border-[#E5E1D8] sm:pr-6">
                    <CalendarDays className="w-5 h-5 text-[#C15C3D] sm:hidden" />
                    <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#C15C3D]">
                      {e.is_past ? "Concluso" : "In programma"}
                    </div>
                    <div className="font-display text-lg sm:text-2xl font-black text-[#1A1A1A] leading-tight">
                      {e.date_label}
                    </div>
                    {e.time_label && (
                      <div className="text-xs sm:text-sm text-[#5C5C5C]">{e.time_label}</div>
                    )}
                  </div>
                  <div className="sm:col-span-7">
                    <h3 className="font-display text-xl sm:text-2xl font-black text-[#1A1A1A] leading-tight">
                      {e.title}
                    </h3>
                    <div className="text-sm text-[#5C5C5C] mt-1.5 font-semibold">{e.location}</div>
                    <p className="mt-3 text-sm sm:text-base text-[#5C5C5C] leading-relaxed line-clamp-2">{e.description}</p>
                  </div>
                  <div className="sm:col-span-2 flex sm:justify-end">
                    {e.link ? (
                      <Link to={e.link.startsWith("/") ? e.link : "/eventi"} className="inline-flex items-center gap-2 text-sm font-bold text-[#2E4F3B] hover:text-[#1f3828] group-hover:gap-3 transition-all">
                        Scopri
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    ) : (
                      <span className="text-xs text-[#5C5C5C] italic">Dettagli in arrivo</span>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SECTION 5 — Sostieni strip (Diventa socio) */}
      <section data-testid="home-sostieni" className="relative py-16 sm:py-20 bg-[#FBF9F6] border-y border-[#E5E1D8]">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-4">
            <span className="w-10 h-px bg-[#C15C3D]" />
            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#C15C3D]">
              Unisciti a noi
            </span>
            <span className="w-10 h-px bg-[#C15C3D]" />
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#1A1A1A] leading-[1.05] tracking-tight max-w-3xl mx-auto">
            Diventa parte di <span className="italic font-light text-[#2E4F3B]">Per Asciano.</span>
          </h2>
          <p className="mt-5 text-base sm:text-lg text-[#5C5C5C] max-w-2xl mx-auto leading-relaxed">
            Diventa socio o candidati come volontario: bastano pochi minuti per fare la differenza sul territorio.
          </p>
          <div className="mt-9 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dona" data-testid="home-sostieni-dona" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C15C3D] hover:bg-[#a64830] text-white font-bold px-7 py-4 transition-all shadow-lg">
              <Heart className="w-4 h-4" fill="currentColor" />
              Fai una donazione
            </Link>
            <Link to="/associarsi" data-testid="home-sostieni-soci" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#2E4F3B] hover:bg-[#1f3828] text-white font-bold px-7 py-4 transition-all shadow-lg">
              Diventa socio (gratuito)
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/partecipa" data-testid="home-sostieni-volontari" className="inline-flex items-center justify-center gap-2 rounded-full bg-white border-2 border-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white text-[#1A1A1A] font-bold px-7 py-4 transition-all">
              Diventa volontario
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* SECTION 6 — Progetti */}
      <section data-testid="home-projects" className="relative py-20 sm:py-28 bg-white">
        <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="flex items-end justify-between gap-6 mb-10 sm:mb-14">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <span className="w-10 h-px bg-[#C15C3D]" />
                <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#C15C3D]">
                  I nostri progetti
                </span>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#1A1A1A] leading-[1.05] tracking-tight">
                I progetti che <span className="italic font-light text-[#2E4F3B]">stiamo realizzando.</span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-[#5C5C5C] leading-relaxed">
                Iniziative concrete per la tutela e la valorizzazione del territorio di Asciano e del Monte Faeta.
              </p>
            </div>
            <Link to="/progetti" data-testid="home-projects-all" className="hidden sm:inline-flex items-center gap-2 text-sm font-bold text-[#1A1A1A] hover:text-[#2E4F3B] underline underline-offset-4">
              Tutti i progetti
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {projects.length === 0 ? (
            <div className="text-center py-12 text-[#5C5C5C] italic">Nessun progetto in evidenza.</div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
              {projects.map((p) => (
                <ProjectCard key={p.id} project={p} testidPrefix="home-project" />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SECTION 7 — FAQ */}
      <section data-testid="home-faq" className="relative py-20 sm:py-28 bg-[#FBF9F6] border-t border-[#E5E1D8]">
        <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <span className="w-10 h-px bg-[#C15C3D]" />
              <span className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.32em] text-[#C15C3D]">
                Domande frequenti
              </span>
              <span className="w-10 h-px bg-[#C15C3D]" />
            </div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black text-[#1A1A1A] leading-[1.05] tracking-tight">
              Rispondiamo alle <span className="italic font-light text-[#2E4F3B]">vostre domande.</span>
            </h2>
          </div>

          <div className="divide-y divide-[#E5E1D8] border-y-2 border-[#1A1A1A]/15">
            {faqs.map((f) => {
              const open = openFaq === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  data-testid={`faq-item-${f.id}`}
                  onClick={() => setOpenFaq(open ? null : f.id)}
                  className="w-full text-left py-5 sm:py-6 group"
                >
                  <div className="flex items-start gap-4">
                    <span className={`mt-1 inline-flex shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors ${open ? "border-[#2E4F3B] bg-[#2E4F3B] text-white" : "border-[#1A1A1A]/30 text-[#1A1A1A]"}`}>
                      <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
                    </span>
                    <div className="flex-1">
                      <h3 className={`font-display text-base sm:text-lg lg:text-xl font-bold leading-snug ${open ? "text-[#2E4F3B]" : "text-[#1A1A1A] group-hover:text-[#2E4F3B]"} transition-colors`}>
                        {f.question}
                      </h3>
                      {open && (
                        <p className="mt-3 text-sm sm:text-base text-[#5C5C5C] leading-relaxed whitespace-pre-line">
                          {f.answer}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <Link to="/faq" data-testid="home-faq-all" className="inline-flex items-center gap-2 text-sm font-bold text-[#1A1A1A] hover:text-[#2E4F3B] underline underline-offset-4">
              Vedi tutte le domande e i link utili
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
